import { MAX_PAGE_SIZE, type ReportGrid } from '@palaccount/contracts';
import { renderDocumentHtml, type CompanyHeader, type DocumentLocale } from '@palaccount/documents';
import { buildXlsx, date, number, safeSheetName, text, XLSX_CONTENT_TYPE, type Cell } from '@palaccount/spreadsheet';

import { downloadDocument } from '@/features/documents/api';
import { printDocumentHtml } from '@/lib/print-document';

import { resolveColumns } from './column-picker';

/**
 * DSH-10 band 1 — the three export buttons (BR-DSH-10-10, BR-DSH-10-11, BR-DSH-10-24).
 *
 * NFR-32 / GEN-43 / GEN-195 — "every list and every report must have an
 * Export action producing a REAL file… XLSX, CSV, and PDF", and the export
 * carries the same rows and columns as the screen (BR-DSH-10-24 by
 * construction — one grid feeds both). The prototype's three export controls
 * produced nothing; these helpers are the file-producing enforcement the rule
 * asked for, and the audit log's CSV button (`useAuditExport`) rides the same
 * `downloadCsv`.
 *
 * All three now start from the SAME grid: the columns and rows on screen, in
 * the reader's language, exactly as BR-DSH-10-24 requires. Print and PDF
 * render it through `@palaccount/documents`; Excel writes it through
 * `@palaccount/spreadsheet`. Nothing re-derives what is displayed, so nothing
 * can disagree with it.
 *
 *  - PRINT renders the document HTML into an offscreen iframe and prints
 *    that — the SAME markup the worker turns into the PDF, so paper and PDF
 *    cannot drift (see lib/print-document.ts).
 *  - EXCEL is a real .xlsx. Numbers are numbers, so a user can sum a column;
 *    dates are dates, so they sort; Arabic survives and an Arabic report opens
 *    right-to-left. CSV stays available for the tools that want it.
 *  - PDF asks the worker and downloads the result (DECISION.md §2.13).
 */

// ── CSV, still offered ──────────────────────────────────────────────────────
//
// KEPT, not left behind. .xlsx is the right default for a person, and CSV is
// still the right answer for a person feeding the figures into something else
// — an accounting package's import, a script, a bank's upload form. They are
// different jobs and the screen offers both rather than choosing.

/** BR-DSH-10-24: the export carries exactly the columns/rows on screen. */
export function toCsv(headers: readonly string[], rows: readonly (readonly (string | number)[])[]): string {
  const quote = (cell: string | number) => {
    const text_ = String(cell);
    return /[",\n]/.test(text_) ? `"${text_.replaceAll('"', '""')}"` : text_;
  };
  return [headers.map(quote).join(','), ...rows.map((row) => row.map(quote).join(','))].join('\n');
}

export function downloadCsv(filename: string, headers: readonly string[], rows: readonly (readonly (string | number)[])[]): void {
  // The UTF-8 BOM keeps Arabic intact when Excel opens the file.
  save(filename, new Blob([`\uFEFF${toCsv(headers, rows)}`], { type: 'text/csv;charset=utf-8' }));
}

// ── Excel ───────────────────────────────────────────────────────────────────

/**
 * Which columns hold what.
 *
 * A report cell can be money, a count, a date, a percentage or a name
 * depending on the centre, and the screen is the only thing that knows which.
 * So the caller says — and the two things it must say are the ones CSV could
 * never express: which columns are FIGURES (so `SUM` works) and which are
 * DATES (so sorting works).
 */
export interface ColumnTypes {
  /** Indexes into `columns` holding a decimal string. */
  numeric?: readonly number[];
  /** Indexes into `columns` holding an ISO date. */
  dates?: readonly number[];
}

export function downloadXlsx(
  filename: string,
  sheetName: string,
  headers: readonly string[],
  rows: readonly (readonly string[])[],
  types: ColumnTypes = {},
  rightToLeft = false,
): void {
  const numeric = new Set(types.numeric ?? []);
  const dates = new Set(types.dates ?? []);

  const bytes = buildXlsx({
    name: safeSheetName(sheetName),
    columns: headers,
    rightToLeft,
    rows: rows.map((row) =>
      headers.map((_, index): Cell => {
        const raw = row[index] ?? '';
        if (dates.has(index)) return date(raw);
        // The grouped, symbol-bearing display string ("$3,374.10") is for
        // reading; a spreadsheet wants the bare decimal, or the cell is text
        // again and the column sums to zero.
        if (numeric.has(index)) return number(bareNumber(raw));
        return text(raw);
      }),
    ),
  });
  save(filename, new Blob([bytes as BlobPart], { type: XLSX_CONTENT_TYPE }));
}

/**
 * "$3,374.10" → "3374.10"; "-12.30" → "-12.30"; "42%" → "42".
 *
 * Undoes exactly what `formatMoney` did, on the string, without parsing.
 *
 * THE `U+2212` REPLACEMENT STAYS EVEN THOUGH `formatMoney` NO LONGER EMITS ONE.
 * It used to, and this line is why that mistake cost so little: every figure
 * that reached a spreadsheet came through here and went back to ASCII, so the
 * .xlsx export was never wrong. Keeping it costs one `replace` and buys three
 * things — `ledgerQty` (MFG-04's signed quantity column) still writes `U+2212`
 * deliberately and may one day be exported; a report card's value can be any
 * string a screen composed; and a hand-pasted figure from an older build is
 * still a real input. A boundary that normalises is worth more than a boundary
 * that assumes, which is the whole lesson of the character it is cleaning up.
 */
export function bareNumber(display: string): string {
  const cleaned = display
    .replace(/−/g, '-')
    .replace(/[^0-9.-]/g, '');
  return /^-?\d+(\.\d+)?$/.test(cleaned) ? cleaned : '';
}

// ── Print and PDF, from the one template ────────────────────────────────────

export interface ReportDocument {
  grid: ReportGrid;
  locale: DocumentLocale;
  company: CompanyHeader;
  /** NFR-33 — the signed-in person, named in the printed footer. */
  generatedBy?: string | null;
}

/** Print the report as the document it will be, not as the screen it is on. */
export function printReportDocument({ grid, locale, company, generatedBy }: ReportDocument): void {
  printDocumentHtml(
    renderDocumentHtml({
      locale,
      company,
      generatedAt: new Date().toISOString(),
      generatedBy: generatedBy ?? null,
      payload: {
        kind: 'report',
        data: {
          title: grid.title,
          // Spread as a pair, so an as-of-now grid prints no window rows at
          // all rather than two empty ones (A14; see ReportGridSchema).
          ...(grid.from === undefined || grid.to === undefined
            ? {}
            : { from: grid.from, to: grid.to }),
          cards: grid.cards.map((card) => ({ label: card.label, value: card.value, changePct: card.changePct ?? null })),
          columns: grid.columns,
          ...(grid.numericColumns ? { numericColumns: grid.numericColumns } : {}),
          rows: grid.rows,
          // BR-DSH-10-16: the heading block's "filters applied" and the
          // configuration in use, exactly as the grid carries them.
          ...(grid.filters ? { filters: grid.filters } : {}),
          ...(grid.configName ? { configName: grid.configName } : {}),
        },
      },
    }),
  );
}

/** Ask the worker for the PDF of exactly this grid, then download it. */
export async function downloadReportPdf(grid: ReportGrid, locale: DocumentLocale): Promise<void> {
  await downloadDocument({ kind: 'report', locale, grid });
}

/**
 * `window.print()` on the live screen.
 *
 * Kept for the screens whose Print is genuinely "print what I am looking at"
 * — the POS receipt strip. A REPORT does not use this any more: it prints its
 * document, so the paper and the PDF are the same artifact.
 */
export function printScreen(): void {
  window.print();
}

function save(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

// ── One grid, four screens ──────────────────────────────────────────────────

/**
 * The grid Print, Excel and PDF all start from.
 *
 * It is built from the SAME three inputs `DetailTable` draws from — the column
 * definitions, the user's selection, and the delivered defaults — and resolved
 * through the SAME `resolveColumns`. That is what makes BR-DSH-10-24 ("the
 * export carries exactly the columns/rows on screen") a property of the code
 * rather than a promise: if the picker hid a column, the export cannot have
 * it, because both asked the same function which columns are showing.
 *
 * Values are the RAW strings — "3374.10", "2026-07-07" — not the formatted
 * cells. Each consumer formats: the document groups and adds a symbol, the
 * spreadsheet keeps the bare decimal so a column sums. Handing over
 * "$3,374.10" and unpicking it later is how a total becomes text.
 */
export interface DetailColumnDef {
  id: string;
  label: string;
  numeric?: boolean | undefined;
}

export interface GridInput<Row> {
  title: string;
  /**
   * The window, or absent on an as-of-now report (DSH-07, delegated ruling
   * A14). Both or neither — `ReportGridSchema` refuses a half-window.
   */
  from?: string;
  to?: string;
  cards: readonly { label: string; value: string; changePct?: string | null }[];
  columnDefs: readonly DetailColumnDef[];
  selected: readonly string[];
  defaults: readonly string[];
  rows: readonly Row[];
  /** The RAW value of one column on one row. */
  value: (row: Row, columnId: string) => string;
  /** Column ids holding an ISO date, so Excel writes a real date cell. */
  dateColumns?: readonly string[];
  /**
   * BR-DSH-10-16 — the filters in force when the grid was built, already
   * labelled in the reader's language, for the printed heading block. The
   * page supplies them for the same reason it supplies the cards: only it
   * knows which filters it applied and what they are called on screen.
   */
  filters?: readonly { label: string; value: string }[];
  /** DSH-10 Band 6: the saved configuration in use, if the reader is on one. */
  configName?: string | null;
}

export interface ReportGridWithTypes {
  grid: ReportGrid;
  types: ColumnTypes;
  /** Column ids, in the order shown — for a caller that needs them. */
  columnIds: readonly string[];
}

export function buildReportGrid<Row>(input: GridInput<Row>): ReportGridWithTypes {
  const active = resolveColumns(
    input.columnDefs.map((c) => ({ id: c.id, labelKey: c.label })),
    input.selected,
    input.defaults,
  );
  const byId = new Map(input.columnDefs.map((c) => [c.id, c]));
  const dates = new Set(input.dateColumns ?? []);

  const numeric: number[] = [];
  const dateIndexes: number[] = [];
  active.forEach((id, index) => {
    if (dates.has(id)) dateIndexes.push(index);
    else if (byId.get(id)?.numeric) numeric.push(index);
  });

  return {
    columnIds: active,
    types: { numeric, dates: dateIndexes },
    grid: {
      title: input.title,
      // Spread rather than assigned, so an as-of-now report omits the keys
      // instead of sending `undefined` — `.strict()` accepts a missing key and
      // an explicit `undefined` is the same value with a different shape, and
      // only one of the two survives JSON on the way to the PDF worker.
      ...(input.from === undefined || input.to === undefined
        ? {}
        : { from: input.from, to: input.to }),
      cards: input.cards.map((card) => ({
        label: card.label,
        value: card.value,
        changePct: card.changePct ?? null,
      })),
      columns: active.map((id) => byId.get(id)?.label ?? id),
      numericColumns: [...numeric, ...dateIndexes],
      rows: input.rows.map((row) => active.map((id) => input.value(row, id))),
      // BR-DSH-10-16: the heading block's provenance travels IN the grid, so
      // Print and PDF cannot disagree with each other about which filters
      // were in force — both read the one object.
      ...(input.filters && input.filters.length > 0 ? { filters: [...input.filters] } : {}),
      ...(input.configName ? { configName: input.configName } : {}),
    },
  };
}

// ── Every list's Excel export, once (wave 3) ────────────────────────────────

/**
 * The most rows one click may ask for. Generalised from the invoices list's
 * own export (retired in wave 3B), whose `EXPORT_ROW_CAP` carried the reason:
 * a register of a hundred thousand documents cannot be fetched in one click,
 * so the walk stops here and the summary line says the cap cut it.
 */
export const LIST_EXPORT_ROW_CAP = 5000;

/**
 * Walk a paged endpoint page by page at the contract's ceiling — never the
 * unbounded opt-out (NFR-42) — until the register is exhausted or `cap` rows
 * are in hand. `fetchPage` is the caller's typed ts-rest call; what comes
 * back is normalised to `rows` so the walk does not care whether the body
 * calls them `orders`, `invoices` or `notes`.
 */
export async function fetchAllPages<Row>(
  fetchPage: (page: number, pageSize: number) => Promise<{ rows: readonly Row[]; total: number }>,
  cap = LIST_EXPORT_ROW_CAP,
  pageSize = MAX_PAGE_SIZE,
): Promise<{ rows: Row[]; total: number; capped: boolean }> {
  const rows: Row[] = [];
  let total: number;
  for (let page = 1; ; page += 1) {
    const result = await fetchPage(page, pageSize);
    total = result.total;
    rows.push(...result.rows);
    if (result.rows.length === 0 || rows.length >= total || rows.length >= cap) break;
  }
  const capped = rows.length > cap || total > cap;
  return { rows: rows.slice(0, cap), total, capped };
}

/**
 * One column of a list export: the translated header, the RAW cell value
 * (a bare decimal, an ISO date, a code — never the formatted display string,
 * for the reason `buildReportGrid` gives), and what kind of cell to write so
 * a figure sums and a date sorts.
 */
export interface ListExportColumn<Row> {
  header: string;
  value: (row: Row) => string;
  kind?: 'text' | 'number' | 'date' | undefined;
}

/** The rows as the sheet writes them — pure, so a test can read them back. */
export function listExportRows<Row>(
  columns: readonly ListExportColumn<Row>[],
  rows: readonly Row[],
): string[][] {
  return rows.map((row) => columns.map((column) => column.value(row)));
}

/**
 * `filename` is the base name ("sales-orders"); today's date and `.xlsx` are
 * appended, and the same base names the sheet.
 */
export function downloadListXlsx<Row>(
  filename: string,
  columns: readonly ListExportColumn<Row>[],
  rows: readonly Row[],
  rightToLeft: boolean,
): void {
  const numeric: number[] = [];
  const dates: number[] = [];
  columns.forEach((column, index) => {
    if (column.kind === 'number') numeric.push(index);
    else if (column.kind === 'date') dates.push(index);
  });
  downloadXlsx(
    `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`,
    filename,
    columns.map((column) => column.header),
    listExportRows(columns, rows),
    { numeric, dates },
    rightToLeft,
  );
}
